import React, { useState, useEffect, Suspense, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import SubTitle from "../common/SubTitle";
import Cards from "../common/Cards";
import SearchFilter from "../common/SearchFilter";
import Pagination from "../common/Pagination";
import axios from "axios";
import config from "../../config.js";
import { usePermission } from "../../hooks/usePermission";
import { CircularProgress } from "@mui/material";
import sortByParam from "../../hooks/SortByParam";
import searchInTheTable from "../../hooks/SarchInTable";
import filterBy from "../../hooks/FilterBy";
import { CARD_BACKGROUND_COLORS, TEXT_COLORS } from "../../constants/styles";
import { ACTIONS_COLUMN_ID } from "../../constants/dashboardConstants";
import DeleteModal from "../common/DeleteModal";
import { useTranslation } from "react-i18next";
import { useUser } from "../../pages/login/UserContextPersistent";
import InactiveInventoryTable from "../common/InactiveInventoryTable";
import { PAGE_IDS, isJsonDrivenLayoutEnabled } from "../../config/uiLayoutConfig";
import { usePageLayout } from "../../context/LayoutContext";
import {
  FLEET_SITE_ROW_ACTIONS,
  computeFleetSiteMetric,
} from "../../config/sitesModuleCatalog";
import { hasLayoutPermission } from "../../config/vehiclesModuleCatalog";
import {
  buildFleetRowActionsFromLayout,
  buildFleetSiteStatCards,
  buildFilterWidgetsFromLayout,
  buildTableHeadersFromLayout,
  getActionBarWidgets,
  getPageTitleKey,
} from "../../layout/moduleLayoutUtils";
const DynamicTable = React.lazy(() => import("../common/DynamicTable"));

const legacyHeaders = [
  { key: "Site ID", value: "Site ID" },
  { key: "Site", value: "Sites" },
  { key: "Description", value: "Description" },
  { key: "Vehicles", value: "Vehicles" },
  { key: "Site Incharge", value: "Site Incharges" },
  { key: ACTIONS_COLUMN_ID, value: "Actions" },
];

export default function SitesMain({ school = false } = {}) {
  const { t } = useTranslation();
  const [siteList, setSiteList] = useState([]);
  const [sortedTableData, setSortedTableData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    siteId: "",
    name: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [siteIdToDelete, setSiteIdToDelete] = useState(null);
  const [sortParam, setSortParam] = useState("siteId");
  const [sortDirection, setSortDirection] = useState("asc");
  const [sortedExportData, setSortedExportData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const { dispatch, organizationInactive } = useUser();
  const useJsonLayout = isJsonDrivenLayoutEnabled();
  const sitesPageId = school ? PAGE_IDS.SCHOOL_SITES : PAGE_IDS.FLEET_SITES;
  const { layout: moduleLayout } = usePageLayout(
    useJsonLayout ? sitesPageId : null,
  );

  const layoutHeaders = useMemo(() => {
    if (!moduleLayout) return null;
    return buildTableHeadersFromLayout(moduleLayout, { hasPermission, t });
  }, [moduleLayout, hasPermission, t]);

  const layoutRowActions = useMemo(() => {
    if (!moduleLayout) return null;
    return buildFleetRowActionsFromLayout(moduleLayout, { hasPermission }, FLEET_SITE_ROW_ACTIONS);
  }, [moduleLayout, hasPermission]);

  const layoutStatCards = useMemo(() => {
    if (!moduleLayout) return null;
    return buildFleetSiteStatCards(moduleLayout, siteList);
  }, [moduleLayout, siteList]);

  const actionBarWidgets = useMemo(
    () => (moduleLayout ? getActionBarWidgets(moduleLayout) : []),
    [moduleLayout],
  );

  const layoutFilterWidgets = useMemo(() => {
    if (!moduleLayout) return null;
    return buildFilterWidgetsFromLayout(moduleLayout);
  }, [moduleLayout]);

  const tableHeaders = layoutHeaders || legacyHeaders;
  const downloadColumns = tableHeaders
    .filter((header) => header.key !== ACTIONS_COLUMN_ID)
    .map((header) => ({
      key: header.key,
      label: header.value,
    }));

  const getLegacyActions = () =>
    Object.values(FLEET_SITE_ROW_ACTIONS).filter((action) =>
      hasLayoutPermission(action.permission, hasPermission),
    );

  const tableActions = layoutRowActions || getLegacyActions();

  const userCacheRef = useRef({});
  const [siteIdFilterOptions, setSiteIdFilterOptions] = useState([]);
  const [siteNameFilterOptions, setSiteNameFilterOptions] = useState([]);

  const [filterConfig, setFilterConfig] = useState([
    {
      id: "siteId",
      label: "Site ID",
      helperText: "Select Site ID",
      options: [],
    },
    {
      id: "name",
      label: "Site Name",
      helperText: "Select Site Name",
      options: [],
    },
  ]);

  useEffect(() => {
    if (layoutFilterWidgets) {
      setFilterConfig(
        layoutFilterWidgets.map((widget) => ({
          id: widget.filterKey,
          label: widget.labelKey,
          helperText: `Select ${String(widget.labelKey || "").toLowerCase()}`,
          options:
            widget.filterKey === "siteId"
              ? siteIdFilterOptions
              : widget.filterKey === "name"
                ? siteNameFilterOptions
                : (widget.options || []).map((option) => ({
                    value: option.value,
                    label: option.label,
                  })),
        })),
      );
      return;
    }

    setFilterConfig([
      {
        id: "siteId",
        label: "Site ID",
        helperText: "Select Site ID",
        options: siteIdFilterOptions,
      },
      {
        id: "name",
        label: "Site Name",
        helperText: "Select Site Name",
        options: siteNameFilterOptions,
      },
    ]);
  }, [layoutFilterWidgets, siteIdFilterOptions, siteNameFilterOptions]);

  async function getAllSites() {
    try {
      // Get organizationId from userState in localStorage
      let organizationId;
      const userStateString = localStorage.getItem("userState");
      if (userStateString) {
        const userState = JSON.parse(userStateString);
        organizationId = userState.user.organizationId;
      }

      if (!organizationId) {
        throw new Error("Organization ID not found in localStorage");
      }

      const response = await axios.get(
        `${config.baseURL}/site/organization?organizationId=${organizationId}`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.log("Error in fetching site data: ", error);
      throw error;
    }
  }

  // Function to delete site
  async function deleteSite(id) {
    try {
      const response = await axios.delete(`${config.baseURL}/site/`, {
        data: { id },
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      console.log("Error in deleting site data: ", error);
      throw error;
    }
  }

  // function to fetch user details
  async function getUserById(userId) {
    try {
      // Check cache first
      if (userCacheRef.current[userId]) {
        return userCacheRef.current[userId];
      }

      const response = await axios.get(`${config.baseURL}/user/${userId}`, {
        withCredentials: true,
      });

      // Cache the user data
      userCacheRef.current[userId] = response.data;

      return response.data;
    } catch (error) {
      console.log("Error fetching user data: ", error);
      return null;
    }
  }

  async function fetchSiteInchargeNames(sites) {
    const uniqueInchargeIds = [
      ...new Set(sites.map((site) => site.siteIncharge).filter(Boolean)),
    ];

    await Promise.all(uniqueInchargeIds.map((userId) => getUserById(userId)));

    return sites.map((site) => {
      if (!site.siteIncharge) {
        return {
          ...site,
          siteInchargeName: "N/A",
        };
      }

      const user = userCacheRef.current[site.siteIncharge];

      return {
        ...site,
        siteInchargeName: user?.user?.name || "Unknown",
      };
    });
  }

  // Fetch sites data on component mount
  useEffect(() => {
    let isMounted = true;

    const fetchSites = async () => {
      setIsLoading(true);

      try {
        const sites = await getAllSites();
        // Fetch site incharge names
        const sitesWithNames = await fetchSiteInchargeNames(sites);

        if (!isMounted) {
          return;
        }

        setSiteList(sitesWithNames);

        // Extract unique siteIds for filter
        const siteIds = [];
        const siteIdSet = new Set();
        sites.forEach((site) => {
          if (site.siteId && !siteIdSet.has(site.siteId)) {
            siteIdSet.add(site.siteId);
            siteIds.push({
              value: site.siteId,
              label: site.siteId,
            });
          }
        });

        // Extract unique site names for filter
        const siteNames = [];
        const siteNameSet = new Set();
        sites.forEach((site) => {
          if (site.name && !siteNameSet.has(site.name)) {
            siteNameSet.add(site.name);
            siteNames.push({
              value: site.name,
              label: site.name,
            });
          }
        });

        setSiteIdFilterOptions(siteIds);
        setSiteNameFilterOptions(siteNames);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        if (error.response && error.response.status === 401) {
          console.log("Unauthorized request");
          dispatch({ type: "CLEAR_USER" });
          navigate("/login", { replace: true });
          return;
        }
        console.error("Error fetching site data:", error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchSites();

    return () => {
      isMounted = false;
    };
  }, [dispatch, navigate]);

  const sortSites = (list, param, direction) => {
    const sorted =
      param === "createdAt"
        ? [...list].sort((a, b) => {
            const aTime =
              new Date(
                a.createdAt || a.created_at || a.updatedAt || a.updated_at || 0,
              ).getTime() || 0;
            const bTime =
              new Date(
                b.createdAt || b.created_at || b.updatedAt || b.updated_at || 0,
              ).getTime() || 0;
            if (aTime !== bTime) return aTime - bTime;
            return Number(a.id || 0) - Number(b.id || 0);
          })
        : sortByParam(param, list);

    return direction === "desc" ? [...sorted].reverse() : sorted;
  };

  // Apply sorting, filtering and searching whenever relevant state changes
  useEffect(() => {
    let filteredData = [...siteList];

    // Apply search
    if (searchQuery) {
      filteredData = searchInTheTable(searchQuery, filteredData);
    }

    // Apply filters
    if (filters.siteId) {
      filteredData = filterBy("siteId", filters.siteId, filteredData);
    }

    if (filters.name) {
      filteredData = filterBy("name", filters.name, filteredData);
    }

    // Apply sorting. Newest sites are first unless a column sort is chosen.
    filteredData = sortSites(filteredData, sortParam, sortDirection);

    setSortedTableData(filteredData);
  }, [searchQuery, filters, sortParam, sortDirection, siteList]);

  // Format data for table display (memoized to avoid re-renders)
  const formattedTableData = useMemo(() => {
    if (!sortedTableData) return [];
    return sortedTableData.map((site) => {
      const formattedData = {
        "Site ID": site.siteId || "N/A",
        Site: site.name || "N/A",
        "Site Incharge": site.siteInchargeName || "N/A",
        Description: site.description || "N/A",
        Vehicles: site.vehicles
          ? site.vehicles.map((vehicle) => vehicle.name).join(", ")
          : "N/A",
      };
      // Add original ID for action handling
      formattedData.id = site.id;
      formattedData.originalData = site;

      return formattedData;
    });
  }, [sortedTableData]);

  const handleSortedDataChange = useCallback((newSortedData) => {
    setSortedExportData((prevData) => {
      if (prevData === newSortedData) return prevData;
      if (!prevData || !newSortedData) return newSortedData;
      if (prevData.length === newSortedData.length) {
        const isSame = prevData.every((row, idx) => {
          const newRow = newSortedData[idx];
          return row === newRow || (row.id !== undefined && row.id === newRow.id);
        });
        if (isSame) return prevData;
      }
      return newSortedData;
    });
  }, []);

  // Handle search changes
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(1); // Reset to first page when search changes
  };

  // Handle filter changes
  const handleFilterChange = (filterValues) => {
    setFilters(filterValues);
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Handle sort change
  const handleSortChange = (param) => {
    const newSortDirection =
      sortParam === param ? (sortDirection === "asc" ? "desc" : "asc") : "asc";
    setSortDirection(newSortDirection);
    setSortParam(param);
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Calculate pagination indexes
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedTableData.slice(indexOfFirstItem, indexOfLastItem);

  // Add site handler
  const handleAddSite = () => {
    navigate(school ? "/school-sites/add" : "/site/add");
  };

  // Handle action clicks
  const handleActionClick = (actionId, rowIndex, actionObj) => {
    // Row index is for the same array passed to DynamicTable (full sorted list), not a page slice.
    const site = sortedTableData[rowIndex];
    if (!site?.id) {
      console.warn("[SitesMain] Action click: no site id for row", rowIndex, site);
      return;
    }

    switch (actionId) {
      case "update":
        navigate(school ? `/school-sites/${site.id}` : `/site/${site.id}`);
        break;
      case "delete":
        setSiteIdToDelete(site.id);
        setDeleteModalOpen(true);
        break;
      default:
        break;
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    try {
      if (siteIdToDelete) {
        await deleteSite(siteIdToDelete);
        setSiteList((prevList) =>
          prevList.filter((site) => site.id !== siteIdToDelete)
        );
        // Show toast notification or success message here
      }
    } catch (error) {
      console.error("Error deleting site:", error);
      // Show error notification here
    } finally {
      setDeleteModalOpen(false);
      setSiteIdToDelete(null);
    }
  };

  // Handle delete cancellation
  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSiteIdToDelete(null);
  };
  const createAction = actionBarWidgets.find((widget) => widget.actionKey === "createSite");
  const canCreateSite = createAction
    ? hasLayoutPermission(createAction.permission, hasPermission)
    : hasPermission("/api/site", "POST");
  const pageTitle = getPageTitleKey(moduleLayout) || "Sites";
  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization?.name;

  const legacyStatCards = [
    {
      title: "Total Sites",
      value: computeFleetSiteMetric("totalSites", siteList),
      backgroundColor: CARD_BACKGROUND_COLORS.DARK_BLUE,
      titleColor: TEXT_COLORS.WHITE,
      valueColor: TEXT_COLORS.WHITE,
    },
  ];

  return (
    <div style={{ minHeight: "100vh" }}>
      <SubTitle
        title={pageTitle}
        path={!school ? `${orgName || "organization"} > Sites` : undefined}
        add={!organizationInactive && canCreateSite ? createAction?.labelKey || "+ Create Sites" : null}
        addPath={school ? "/school-sites/add" : "/site/add"}
        onAddClick={!organizationInactive && canCreateSite ? handleAddSite : null}
      />

      {organizationInactive ? (
        <div className="table_container">
          <InactiveInventoryTable
            title="siteInventory"
            headers={tableHeaders}
            data={formattedTableData}
            loading={isLoading}
            emptyMessage={t("noDataFound")}
            itemLabel="sites"
          />
        </div>
      ) : (
        <>
          <Cards cardData={layoutStatCards || legacyStatCards} />

          <div className="table_container">
            <SearchFilter
              filters={filterConfig}
              searchPlaceholder="Search"
              onSearchChange={handleSearchChange}
              onFilterChange={handleFilterChange}
              dataDownload={true}
              downloadRows={
                sortedExportData.length > 0
                  ? sortedExportData
                  : formattedTableData
              }
              downloadColumns={downloadColumns}
              downloadFileName={school ? "school-sites" : "sites"}
            />

        <Suspense fallback={<CircularProgress />}>
          <DynamicTable
            headers={tableHeaders}
            data={formattedTableData}
            onActionClick={handleActionClick}
            onSortChange={handleSortChange}
            onSortedDataChange={handleSortedDataChange}
            sortParam={sortParam}
            sortDirection={sortDirection}
            actions={tableActions}
            loading={isLoading}
          />
        </Suspense>

        {/* <Pagination
                    totalItems={sortedTableData?.length || 0}
                    itemsPerPage={itemsPerPage}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                /> */}
      </div>
        </>
      )}

      {/* Delete Confirmation Dialog */}

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Site!"
        message={
          <>
            {t("Are you sure you want to delete")}{" "}
            <span style={{ fontWeight: 600, color: "#d32f2f" }}>
              {siteIdToDelete?.name || t("this site")}
            </span>
            ? {t("This action cannot be undone.")}
          </>
        }
      />
    </div>
  );
}
